import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';

const Header = ({ onNavigateToLibrary, onLogout }) => {
    const [scrolled, setScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 30);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleProfileClick = () => {
        if (onNavigateToLibrary) {
            onNavigateToLibrary();
        }
        setIsMobileMenuOpen(false);
    };

    const handleLogoutClick = () => {
        if (onLogout) {
            onLogout();
        }
        setIsMobileMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    return (
        <header className="sticky top-0 z-50 flex flex-col items-center justify-center mb-12 shadow-xl bg-transparent backdrop-blur-md">
            <div className="relative w-full max-w-2xl flex items-center justify-center py-6 px-8 mb-2">
                {/* Movie/film icon */}
                <span className={`mr-4 text-[#e50914] transition-transform duration-500 ${scrolled ? 'scale-110' : 'scale-100'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-10 h-10">
                        <rect x="3" y="5" width="18" height="14" rx="3" className="fill-[#e50914]/20" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M7 3v4m10-4v4" />
                    </svg>
                </span>
                <h1 className={`text-5xl font-extrabold text-center text-white drop-shadow-lg tracking-tight transition-transform duration-500 ${scrolled ? 'scale-110' : 'scale-100'}`}>
                    My Movie Vault
                </h1>
            </div>
            <div className="w-24 h-1 bg-[#e50914] mb-4"></div>
            <p
                className={`text-lg text-slate-200 font-medium text-center max-w-xl transition-all duration-500 ${scrolled ? 'opacity-0 scale-75 pointer-events-none' : 'opacity-100 scale-100'}`}
            >
                Discover, collect, and get AI-powered recommendations for your favorite movies.
            </p>
            
            {/* Profile Icon - Positioned at far right of page (Desktop only) */}
            {currentUser && (
                <button
                    onClick={handleProfileClick}
                    className={`hidden md:flex fixed top-4 right-4 items-center space-x-2 bg-[#e50914] hover:bg-[#b0060f] text-white px-4 py-2 transition-all duration-300 transform hover:scale-105 shadow-lg z-50 ${scrolled ? 'scale-90' : 'scale-100'}`}
                    title="Go to Library"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    <span className="hidden sm:inline text-sm font-medium truncate max-w-32">
                        {currentUser.email}
                    </span>
                </button>
            )}

            {/* Logout Button - Positioned at far left of page (Desktop only) */}
            {currentUser && (
                <button
                    onClick={handleLogoutClick}
                    className={`hidden md:flex fixed top-4 left-4 items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 transition-all duration-300 transform hover:scale-105 shadow-lg z-50 ${scrolled ? 'scale-90' : 'scale-100'}`}
                    title="Logout"
                >
                    Logout
                </button>
            )}

            {/* Mobile Hamburger Menu */}
            {currentUser && (
                <div className="md:hidden">
                    {/* Hamburger Button */}
                    <button
                        onClick={toggleMobileMenu}
                        className={`fixed top-4 left-4 z-50 p-2 bg-[#141414] hover:bg-[#333] text-white transition-all duration-300 ${scrolled ? 'scale-90' : 'scale-100'}`}
                        title="Menu"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                        </svg>
                    </button>

                    {/* Mobile Menu Overlay */}
                    {isMobileMenuOpen && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleMobileMenu}>
                            <div className="fixed top-0 left-0 h-full w-64 bg-[#141414] shadow-lg z-50" onClick={(e) => e.stopPropagation()}>
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-white text-lg font-semibold">Menu</h3>
                                        <button
                                            onClick={toggleMobileMenu}
                                            className="text-gray-400 hover:text-white"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                            </svg>
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        <div className="text-gray-400 text-sm mb-4 font-bold">
                                            {currentUser.email}
                                        </div>
                                        
                                        <button
                                            onClick={handleProfileClick}
                                            className="w-full flex items-center space-x-3 p-3 text-white bg-[#141414] hover:bg-[#333] transition-colors duration-200 border border-gray-600"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                            </svg>
                                            <span>My Library</span>
                                        </button>
                                        
                                        <button
                                            onClick={handleLogoutClick}
                                            className="w-full flex items-center space-x-3 p-3 text-white bg-red-600 hover:bg-red-700 transition-colors duration-200"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                                            </svg>
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </header>
    );
};

export default Header;
